import { highlightMatches } from '../KnowledgeFileSearchBar/highlightMatches';

// Words kept on each side of the match, widest first. More context pins the
// click to that exact occurrence; less survives text the PDF lays out differently.
const CONTEXT_WORD_COUNTS = [4, 2];

// Snippets come from the markdown the PDF was converted to at ingestion, so drop
// the markdown syntax that is not in the PDF's own text.
const toPlainText = (markdown: string): string =>
  markdown
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1') // [text](url) -> text
    .replace(/[*_#`>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Phrases to search the PDF for to land on the match a snippet shows, best
 * first, ending with the bare query as the fallback.
 */
export const snippetFindPhrases = (
  snippet: string,
  query: string
): string[] => {
  const trimmedQuery = query.trim();
  const truncatedStart = snippet.startsWith('…');
  const truncatedEnd = snippet.endsWith('…');
  const text = toPlainText(snippet.replace(/^…|…$/g, ''));

  const segments = highlightMatches(text, trimmedQuery);
  const matchIndex = segments.findIndex(segment => segment.isMatch);
  if (matchIndex === -1) return [trimmedQuery];

  const beforeText = segments
    .slice(0, matchIndex)
    .map(segment => segment.text)
    .join('');
  const afterText = segments
    .slice(matchIndex + 1)
    .map(segment => segment.text)
    .join('');
  const before = beforeText.split(' ').filter(Boolean);
  const after = afterText.split(' ').filter(Boolean);
  // A word cut by the snippet's edge is not a whole word in the PDF
  if (truncatedStart) before.shift();
  if (truncatedEnd) after.pop();
  // Keep a word glued to the match, e.g. "(" in "(acme.com)", glued
  const beforeGap = beforeText.endsWith(' ') ? ' ' : '';
  const afterGap = afterText.startsWith(' ') ? ' ' : '';
  const match = segments[matchIndex].text;

  const phrases = CONTEXT_WORD_COUNTS.map(count => {
    const left = before.slice(-count).join(' ');
    const right = after.slice(0, count).join(' ');
    return `${left}${left && beforeGap}${match}${right && afterGap}${right}`;
  });
  return Array.from(new Set([...phrases, trimmedQuery]));
};
